import { useRouter } from 'next/router';

import { Cart, TCartPoints } from '../../../../types/cart';
import { useWalletAccess } from '../../../hooks/use-wallet';
import { isWalletTenant } from '../../../utils/wallet-access';

import formatWalletAmount from './format-amount';

export const DEFAULT_ACCUMULATION_CURRENCY: TWalletCurrency = {
  code: 'EUR',
  symbol: '€',
};

/**
 * Whether the tag is reserved for companies that actually have a wallet. **On**:
 * accumulation is only promised to who can actually receive it, so the label
 * follows `canAccessWallet()` — an account with `has_wallet` (and PRO, while the
 * rollout says so), outside Service Box. Flip to `false` to show the design to
 * every account again.
 */
export const ACCUMULATION_REQUIRES_WALLET = true;

/**
 * Whether accumulation may be promised to this user at all. Two independent
 * gates: the storefront (Service Box never shows wallet UI — a ban the rollout
 * flag below must not be able to lift) and, when the flag is on, the account.
 */
const useAccumulationAllowed = (): boolean => {
  const hasWallet = useWalletAccess();

  return isWalletTenant() && (!ACCUMULATION_REQUIRES_WALLET || hasWallet);
};

/**
 * Formats an accumulation amount, or returns `null` when nothing should be
 * rendered. Amount and currency will both come from the accumulation payload
 * once the backend defines it — the default currency is the euro.
 */
const useAccumulation = (
  value?: number | string | null,
  currency: TWalletCurrency = DEFAULT_ACCUMULATION_CURRENCY
): string | null => {
  const { locale = 'en' } = useRouter();
  const isAllowed = useAccumulationAllowed();

  if (!isAllowed) {
    return null;
  }

  return formatWalletAmount(value, currency, locale, { alwaysCents: true });
};

/**
 * Product statuses that never accumulate **before** the purchase: a part that is
 * sold or reserved is not going to credit the wallet, so promising it would be a
 * lie (PLP, product page, cart, checkout).
 */
export const ACCUMULATION_BLOCKED_STATUSES = ['SOL', 'RSV'];

/**
 * Order statuses where the money has not moved yet — a budget waiting for
 * approval, or an order waiting for payment.
 */
export const PAYMENT_PENDING_STATUS_CODES = ['Budget', 'Payment'];

/**
 * Whether an order has yet to be paid. Post-purchase screens still show it in
 * the future tense: the wallet is only credited (and the total only charged) on
 * payment, so "accumulates"/"total to pay" instead of "accumulated"/"total paid".
 */
export const isPaymentPending = (statusCode?: string | null): boolean =>
  !!statusCode && PAYMENT_PENDING_STATUS_CODES.includes(statusCode);

/**
 * Tense of an accumulation label. Future everywhere before the purchase, and on
 * post-purchase screens past — unless the order has not been paid yet. An
 * unknown status counts as paid: only the pending states above hold it back.
 */
export const getAccumulationTense = (
  statusCode?: string | null,
  { afterPurchase = false }: { afterPurchase?: boolean } = {}
): 'BEFORE' | 'AFTER' =>
  afterPurchase && !isPaymentPending(statusCode) ? 'AFTER' : 'BEFORE';

/**
 * Whether a product status forbids showing an accumulation amount.
 *
 * Nothing blocks it **after** the purchase: inside an order those same codes
 * describe the customer's own part — reserved for their order while it waits for
 * payment (`RSV`), sold to them once paid (`SOL`, confirmed live on 2026-08-20
 * on a `PCO` order) — so blocking there would hide the accumulation on
 * everything they actually bought.
 */
export const isAccumulationBlockedStatus = (
  status?: string | null,
  { afterPurchase = false }: { afterPurchase?: boolean } = {}
): boolean =>
  !afterPurchase && !!status && ACCUMULATION_BLOCKED_STATUSES.includes(status);

/**
 * What a single product accumulates — used by the listing cards, the product
 * page image and the cart/checkout product rows.
 *
 * Real data, already formatted by the API: `pricing.points.points_printable`.
 * Returns `null` when the product carries no points, so the tag disappears
 * instead of showing an invented value, and also for sold/reserved products,
 * which accumulate nothing regardless of what the payload says.
 */
export const useProductAccumulation = (
  // Structural on purpose: the product detail, the list item and the cart
  // product each declare their own `pricing` shape, and the tag only ever needs
  // the points block out of it.
  pricing?: { points?: TPricingPoints | null } | null,
  // `STO` | `SOL` | `RSV`, when the surface knows it. Left out means "nothing
  // blocks it" — the products list payload carries no status at all.
  status?: string | null,
  // Thank-you page / order screens, where `RSV` is the normal state.
  { afterPurchase = false }: { afterPurchase?: boolean } = {}
): string | null => {
  const isAllowed = useAccumulationAllowed();

  if (!isAllowed) {
    return null;
  }

  if (isAccumulationBlockedStatus(status, { afterPurchase })) {
    return null;
  }

  return pricing?.points?.points_printable || null;
};

/**
 * What the whole cart accumulates — used by cart/checkout/order summaries.
 *
 * Real data, no mock: the cart payload carries `points.points_display` already
 * formatted. `points.points` is the same value as a number, and
 * `points.points_default_currency_display` tells which currency is actually
 * credited when the cart is not in the shop's default currency.
 */
export const useCartAccumulation = (
  // Two shapes reach this: the cart (checkout, nested `cart.points`) and the
  // finished order (thank-you page), whose payment block is flat and uses the
  // `*_printable` suffix — `payment.points_printable`, confirmed live on
  // 2026-08-20. `Partial` because the cart query hands out partial data while
  // refetching.
  cart?:
    | (Partial<Cart> & {
        payment?:
          | {
              points?: TCartPoints | null;
              points_printable?: string | null;
            }
          | null;
      })
    | null
): string | null => {
  const isAllowed = useAccumulationAllowed();

  if (!isAllowed) {
    return null;
  }

  return (
    cart?.points?.points_display ||
    cart?.payment?.points?.points_display ||
    cart?.payment?.points_printable ||
    null
  );
};

export default useAccumulation;
